// src/pages/Inventory.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAt,
  endAt,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { listInventorySnapshot, rebuildStockAgg } from "../services/inventoryApi";
import { db } from "../services/firebase";

const DEFAULT_LOCATION_ID = "boutique";
const stockKey = (locId, itemId) => `${locId}__${itemId}`;

export default function Inventory() {
  const navigate = useNavigate();

  // ---- liste d’inventaire (table) ----
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [freshPO, setFreshPO] = useState(true); // recalc onOrder depuis PO ouverts

  // ---- panneau Inventaire/Comptage ----
  const [locationId, setLocationId] = useState(DEFAULT_LOCATION_ID);
  const [term, setTerm] = useState(""); // code-barres ou nom
  const [sugs, setSugs] = useState([]); // suggestions nom
  const [scanning, setScanning] = useState(false);
  const [countQty, setCountQty] = useState(""); // quantité comptée (absolue)
  const [message, setMessage] = useState("");
  const [item, setItem] = useState(null); // { id, name, barcode, price, cost }
  const [currentQty, setCurrentQty] = useState(null);
  const inputRef = useRef(null);

  const allIds = useMemo(() => rows.map((w) => w.itemId), [rows]);

  // ------- LISTE INVENTAIRE -------
  async function load() {
    setLoading(true);
    try {
      const data = await listInventorySnapshot({
        locationId: DEFAULT_LOCATION_ID,
        search: q.trim(),
        limitN: 500,
        freshOnOrder: freshPO,
      });
      setRows(data);
    } catch (e) {
      console.error(e);
      alert("Erreur lors du chargement de l'inventaire.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleKey(e) {
    if (e.key === "Enter") load();
  }

  async function handleRebuild() {
    if (
      !window.confirm(
        "Recalculer onOrder depuis les PO + total/perLocation depuis stock ?"
      )
    )
      return;
    setLoading(true);
    try {
      await rebuildStockAgg();
      await load();
    } catch (e) {
      console.error(e);
      alert("Échec du rebuild des agrégats.");
    } finally {
      setLoading(false);
    }
  }

  // ------- INVENTAIRE / COMPTAGE -------

  // charge la qty actuelle du stock quand un item est sélectionné
  useEffect(() => {
    (async () => {
      if (!item?.id) {
        setCurrentQty(null);
        return;
      }
      try {
        const sRef = doc(db, "stock", stockKey(locationId, item.id));
        const snap = await getDoc(sRef);
        setCurrentQty((snap.exists() ? snap.data().qty : 0) || 0);
      } catch {
        setCurrentQty(0);
      }
    })();
  }, [item?.id, locationId]);

  // recherche par code-barres exact, sinon suggestions nom (préfixe)
  async function searchItem() {
    setMessage("");
    const t = term.trim();
    if (!t) return;

    try {
      // 1) CB exact
      const byBarcode = query(
        collection(db, "itemsEK"),
        where("barcode", "==", t),
        limit(1)
      );
      const bSnap = await getDocs(byBarcode);
      if (!bSnap.empty) {
        const d = bSnap.docs[0];
        const it = d.data();
        setItem({
          id: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          price: Number(it.price || 0),
          cost: Number(it.cost || 0),
        });
        setSugs([]);
        return;
      }

      // 2) préfixe sur nameLower
      const lower = t.toLowerCase();
      const byName = query(
        collection(db, "itemsEK"),
        orderBy("nameLower"),
        startAt(lower),
        endAt(lower + "\uf8ff"),
        limit(10)
      );
      const nSnap = await getDocs(byName);
      const arr = [];
      nSnap.forEach((d) => {
        const it = d.data();
        arr.push({
          id: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          price: Number(it.price || 0),
          cost: Number(it.cost || 0),
        });
      });
      setSugs(arr);
      if (!arr.length)
        setMessage("Aucun résultat. Vérifie le code-barres ou le nom.");
    } catch (e) {
      console.error(e);
      setMessage("Erreur de recherche.");
    }
  }

  function pickSuggestion(s) {
    setItem(s);
    setSugs([]);
    setTerm(s.name);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function startScan() {
    setMessage("");
    if (!("BarcodeDetector" in window)) {
      setMessage("Scanner non supporté sur ce navigateur.");
      return;
    }
    try {
      const detector = new window.BarcodeDetector({
        formats: ["ean_13", "code_128", "upc_a", "upc_e"],
      });
      setScanning(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext("2d");

      const loop = async () => {
        if (!scanning) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const bitmap = await createImageBitmap(canvas);
        const codes = await detector.detect(bitmap);
        if (codes.length) {
          const code = codes[0].rawValue;
          stream.getTracks().forEach((t) => t.stop());
          setScanning(false);
          setTerm(code);
          await searchItem();
        } else {
          requestAnimationFrame(loop);
        }
      };
      requestAnimationFrame(loop);
    } catch (e) {
      console.error(e);
      setScanning(false);
      setMessage("Impossible d’ouvrir la caméra.");
    }
  }

  // ajustement absolu (inventaire)
  async function adjustAbsolute() {
    setMessage("");
    if (!item?.id) {
      setMessage("Choisis d’abord un article.");
      return;
    }
    const newQty = Number(countQty);
    if (!Number.isFinite(newQty) || newQty < 0) {
      setMessage("Quantité invalide.");
      return;
    }

    const sRef = doc(db, "stock", stockKey(locationId, item.id));
    const movRef = doc(collection(db, "inventoryMoves"));
    try {
      await runTransaction(db, async (tx) => {
        const sSnap = await tx.get(sRef);
        const oldQty = (sSnap.exists() ? sSnap.data().qty : 0) || 0;
        const delta = newQty - oldQty;

        tx.set(
          sRef,
          {
            locationId,
            itemId: item.id,
            qty: newQty,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        tx.set(movRef, {
          type: "adjust",
          locationId,
          itemId: item.id,
          delta,
          countedQty: newQty,
          at: serverTimestamp(),
          note: "inventory count",
        });
      });

      setCurrentQty(newQty);
      setMessage(
        `✅ Inventaire ajusté : ${item.name} → ${newQty.toLocaleString()} (loc: ${locationId}).`
      );
      setCountQty("");
    } catch (e) {
      console.error(e);
      setMessage("Erreur d’ajustement : " + e.message);
    }
  }

  const itemInfo = useMemo(() => {
    if (!item) return null;
    return {
      title: item.name || "Article",
      barcode: item.barcode || "—",
      price: Number(item.price || 0),
      cost: Number(item.cost || 0),
    };
  }, [item]);

  // helpers pour navigation
  // dans Inventory.jsx
const goToEdit = (itemId) => {
  // /admin/inventory + "item/xxx/edit" ➜ /admin/inventory/item/xxx/edit
  navigate(`item/${itemId}/edit`);
};

const goToStockSheet = (itemId) => {
  navigate(`item/${itemId}/stock`);
};


  return (
    <div className="container">
      {/* EN-TÊTE + commandes liste */}
      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
        <h3 className="m-0">Inventaire</h3>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="freshPO"
              checked={freshPO}
              onChange={(e) => setFreshPO(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="freshPO">
              Prendre en compte les PO en direct
            </label>
          </div>

          <button
            className="btn btn-outline-secondary"
            onClick={load}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />{" "}
                Chargement…
              </>
            ) : (
              "Actualiser"
            )}
          </button>

          <button
            className="btn btn-outline-primary"
            onClick={handleRebuild}
            disabled={loading}
          >
            Rebuilder onOrder
          </button>
        </div>
      </div>

      {/* BLOC INVENTAIRE / COMPTAGE */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="mb-3">Comptage / Ajustement rapide</h5>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3" hidden>
              <label className="form-label">Emplacement</label>
              <input
                className="form-control"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                placeholder="ex: boutique"
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label">Code-barres ou Nom</label>
              <div className="d-flex gap-2 position-relative">
                <input
                  ref={inputRef}
                  className="form-control"
                  placeholder="Scanner ou taper un nom…"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchItem()}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={searchItem}
                >
                  Chercher
                </button>
                <button
                  hidden
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={startScan}
                  disabled={scanning}
                  title="Scanner via caméra"
                >
                  {scanning ? "Scan…" : "Scanner"}
                </button>

                {sugs.length > 0 && (
                  <div
                    className="list-group position-absolute w-100 mt-5 shadow"
                    style={{ zIndex: 5, maxHeight: 260, overflowY: "auto" }}
                  >
                    {sugs.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => pickSuggestion(s)}
                      >
                        <div className="fw-semibold">{s.name}</div>
                        <small className="text-muted">
                          CB: {s.barcode || "—"} · Prix:{" "}
                          {s.price?.toLocaleString?.() || 0} · Coût:{" "}
                          {s.cost?.toLocaleString?.() || 0}
                        </small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-text">
                La recherche “nom” utilise <code>nameLower</code> (préfixe).
              </div>
            </div>

            <div className="col-12 col-md-2">
              <label className="form-label">Quantité comptée</label>
              <input
                type="number"
                className="form-control"
                min="0"
                value={countQty}
                onChange={(e) => setCountQty(e.target.value)}
                placeholder="ex: 12"
              />
            </div>
          </div>

          {/* fiche item sélectionné */}
          <div className="mt-3">
            {!item ? (
              <div className="text-muted">Aucun article sélectionné.</div>
            ) : (
              <>
                <div className="d-flex flex-wrap justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{itemInfo?.title}</h6>
                    <div className="small text-muted">
                      ID: <code>{item.id}</code> · CB: {itemInfo?.barcode} ·
                      Prix:{" "}
                      {itemInfo?.price?.toLocaleString?.() || 0} XOF · Coût:{" "}
                      {itemInfo?.cost?.toLocaleString?.() || 0} XOF
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="small text-muted">
                      Quantité actuelle ({locationId})
                    </div>
                    <div className="fs-5 fw-semibold">
                      {currentQty === null
                        ? "—"
                        : Number(currentQty).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">{message || "—"}</div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setItem(null);
                        setTerm("");
                        setCountQty("");
                        setCurrentQty(null);
                        setSugs([]);
                        setMessage("");
                        inputRef.current?.focus();
                      }}
                    >
                      Réinitialiser
                    </button>
                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={adjustAbsolute}
                      disabled={
                        !item || countQty === "" || Number(countQty) < 0
                      }
                      title="Enregistrer la quantité comptée"
                    >
                      Enregistrer l’inventaire
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* RECHERCHE & TABLE D’ÉTAT */}
      <div className="input-group mb-4">
        <span className="input-group-text">
          <i className="bi bi-search" />
        </span>
        <input
          type="text"
          className="form-control"
          placeholder="Recherche par nom ou code-barres..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKey}
        />
        <button
          className="btn btn-dark"
          onClick={load}
          disabled={loading || !q.trim()}
        >
          Rechercher
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Article</th>
                <th>Code-barres</th>
                <th className="text-end">Boutique</th>
                <th className="text-end">Total</th>
                <th className="text-end">Prix</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.itemId}>
                  <td className="fw-semibold">{r.name || "—"}</td>
                  <td>{r.barcode || "—"}</td>
                  <td className="text-end">
                    {r.boutique?.toLocaleString() || 0}
                  </td>
                  <td className="text-end fw-semibold">
                    {(r.total || 0).toLocaleString()}
                  </td>
                  <td className="text-end">
                    {r.price ? `${r.price.toLocaleString()} FCFA` : "—"}
                  </td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => goToStockSheet(r.itemId)}
                    >
                      Fiche stock
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => goToEdit(r.itemId)}
                    >
                      Éditer
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr>
                  <td colSpan={6} className="text-muted text-center py-4">
                    Aucun article trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-muted small mt-3">
        Le bloc “Comptage / Ajustement rapide” met à jour <code>stock/*</code>{" "}
        (quantité absolue) et consigne un mouvement <code>inventoryMoves</code>{" "}
        <em>type="adjust"</em> avec le <code>delta</code>. La table ci-dessus
        reflète l’état agrégé (achats en cours, stock boutique, total).
      </p>
    </div>
  );
}
